import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import ArticleCard from '@/components/base/ArticleCard';
import { getArticleById, articles } from '@/mocks/articles';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const articleId = id ? parseInt(id) : 0;
  const article = getArticleById(articleId);

  const relatedArticles = articles
    .filter((a) => a.id !== articleId && a.category === article?.category)
    .slice(0, 3);

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="min-h-[60vh] flex items-center justify-center pt-20">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-800">
              <i className="ri-file-unknow-line text-foreground-400 text-2xl"></i>
            </div>
            <h2 className="mt-4 text-xl font-heading font-bold text-foreground-200">文章未找到</h2>
            <Link to="/" className="mt-4 inline-block text-sm text-primary-400 hover:text-primary-300 transition-colors">
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const articleStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.imageUrl,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    publisher: {
      '@type': 'Organization',
      name: '金沙娱乐',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://www.jinshaentertainment.com/article/${article.id}`,
    },
  };

  const webPageStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    url: `https://www.jinshaentertainment.com/article/${article.id}`,
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleStructuredData) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageStructuredData) }}
      />
      <Navbar />
      <main className="pt-20">
        <div className="w-full px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: article.category, path: '/' },
              { label: article.title },
            ]}
          />
        </div>

        <article className="w-full px-4 md:px-6 py-8 md:py-12">
          <div className="max-w-4xl mx-auto">
            <div className="mb-6">
              <span className="inline-block px-3 py-1 text-xs font-medium text-primary-400 bg-primary-500/10 rounded-full mb-4">
                {article.category}
              </span>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-50 leading-tight">
                {article.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-foreground-500">
                <div className="flex items-center gap-1.5">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-user-line text-xs"></i>
                  </div>
                  {article.author}
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-calendar-line text-xs"></i>
                  </div>
                  {article.date}
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-time-line text-xs"></i>
                  </div>
                  {article.readTime}
                </div>
              </div>
            </div>

            <div className="relative w-full h-[300px] md:h-[400px] rounded-xl overflow-hidden mb-8">
              <img
                src={article.imageUrl}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            <div
              className="prose prose-invert max-w-none
                prose-headings:font-heading prose-headings:text-foreground-100 prose-headings:font-bold
                prose-h2:text-xl md:prose-h2:text-2xl prose-h2:mt-8 prose-h2:mb-4
                prose-h3:text-lg md:prose-h3:text-xl prose-h3:mt-6 prose-h3:mb-3
                prose-p:text-foreground-400 prose-p:leading-relaxed prose-p:text-sm md:prose-p:text-base
                prose-strong:text-foreground-200
                prose-a:text-primary-400 prose-a:no-underline hover:prose-a:text-primary-300"
              dangerouslySetInnerHTML={{ __html: article.content }}
            ></div>

            <div className="flex flex-wrap items-center gap-2 mt-8 pt-6 border-t border-background-700/30">
              <span className="text-sm text-foreground-500">标签：</span>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 text-xs text-foreground-400 bg-background-900 border border-background-700/30 rounded-full whitespace-nowrap"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="w-full px-4 md:px-6 py-12 bg-background-900/40">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-50 mb-6">
                相关文章
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {relatedArticles.map((rel) => (
                  <ArticleCard key={rel.id} article={rel} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}